import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { CheckCircle, Clock, AlertTriangle } from 'lucide-react';

const CustomerProjectView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getCustomerProjectView(token)
      .then(res => setData(res.data))
      .catch(() => setError('Projektlänken är ogiltig eller har löpt ut.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="w-8 h-8 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="text-center">
        <AlertTriangle size={40} className="text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-lg font-medium text-foreground">{error}</p>
      </div>
    </div>
  );

  const { project, total_hours_worked, signatures, customer_name } = data;
  const progress = project.progress_percent || 0;
  const progressColor = progress >= 80 ? 'bg-green-500' : progress >= 40 ? 'bg-yellow-500' : 'bg-[hsl(16,100%,55%)]';
  const statusColor = project.status === 'completed' ? 'text-green-700 bg-green-50' : project.status === 'paused' ? 'text-yellow-700 bg-yellow-50' : 'text-blue-700 bg-blue-50';

  const daysRemaining = project.end_date
    ? Math.max(0, Math.ceil((new Date(project.end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="customer-project-view">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-bold tracking-tight text-foreground" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Operio
              </span>
              {customer_name && (
                <span className="text-sm text-muted-foreground ml-3">Hej, {customer_name}</span>
              )}
            </div>
            <span className={`inline-flex px-3 py-1 rounded text-xs font-medium ${statusColor}`}>
              {project.status === 'completed' ? 'Klart' : project.status === 'active' ? 'Pågår' : project.status === 'paused' ? 'Pausat' : project.status}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Project Name & Progress */}
        <div className="bg-white border border-border rounded p-6">
          <h1 className="text-2xl font-bold tracking-tight text-foreground mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {project.name}
          </h1>
          {project.description && <p className="text-sm text-muted-foreground mb-6">{project.description}</p>}

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">Projektprogress</span>
              <span className="font-bold text-foreground">{progress}%</span>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div className={`h-full ${progressColor} rounded-full transition-all duration-500`} style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-border rounded p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Progress</p>
            <p className="text-2xl font-bold text-foreground mt-1">{progress}%</p>
          </div>
          <div className="bg-white border border-border rounded p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Arbetade timmar</p>
            <p className="text-2xl font-bold text-foreground mt-1">{total_hours_worked?.toFixed(1) || 0}</p>
          </div>
          <div className="bg-white border border-border rounded p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Beräknade dagar</p>
            <p className="text-2xl font-bold text-foreground mt-1">{project.estimated_days || '—'}</p>
          </div>
          <div className="bg-white border border-border rounded p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Dagar kvar</p>
            <p className={`text-2xl font-bold mt-1 ${daysRemaining !== null && daysRemaining < 7 ? 'text-destructive' : 'text-foreground'}`}>
              {daysRemaining !== null ? daysRemaining : '—'}
            </p>
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-border rounded p-5">
            <h3 className="text-sm font-bold text-foreground mb-3">Projektdetaljer</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Kund</dt>
                <dd className="font-medium">{project.customer || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Plats</dt>
                <dd className="font-medium">{project.location || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Startdatum</dt>
                <dd className="font-medium">{project.start_date || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Deadline</dt>
                <dd className="font-medium">{project.end_date || '—'}</dd>
              </div>
            </dl>
          </div>

          <div className="bg-white border border-border rounded p-5">
            <h3 className="text-sm font-bold text-foreground mb-3">Signaturer</h3>
            {signatures.length === 0 ? (
              <p className="text-sm text-muted-foreground">Inga signaturer ännu</p>
            ) : (
              <div className="space-y-2">
                {signatures.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="font-medium">{s.signer_name}</span>
                    <span className="text-muted-foreground">— {s.action}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="text-center py-6">
        <p className="text-xs text-muted-foreground">Operio — Digitalt projektverktyg</p>
      </footer>
    </div>
  );
};

export default CustomerProjectView;
